import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import HeaderIconTooltip from '../HeaderIconTooltip/HeaderIconTooltip';
import Icon from '../Icons/Icon';

import { TextInput, CustomSelect, Number } from '../FormInputs/index';

const DistributedItems = ({
  isEdit,
  options,
  distItems = [],
  setDistItems = () => {},
  isShowHeader = true,
  iconSize = undefined,
  labelFontClassName = undefined,
  border = undefined,
  headerBottomMargin = undefined,
  margins = undefined,
  paddingBottom = undefined,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({ category: '', description: '', quantity: '' });
  const [isValidItem, setIsValidItem] = useState(true);
  const [isValidDescription, setIsValidDescription] = useState(true);
  const [isValidCount, setIsValidCount] = useState(true);
  const [showHeader, setShowHeader] = useState(isShowHeader);
  const [errorMessage, setErrorMessage] = useState({ category: '', description: '', quantity: '' });

  const isOtherCategory = (distItem) => distItem?.category?.toLowerCase() === 'other';
  const isNewItemDescriptionRequired = () => isOtherCategory(newItem);

  const handleDelete = (id) => {
    const newItems = distItems.filter(item => item.id !== id);
    setDistItems(newItems);
  };

  const handleAdd = () => {
    let isValid = true;

    if (newItem.category) {
      setIsValidItem(true);
    } else {
      setIsValidItem(false);
      isValid = false;
      setErrorMessage(prev => ({ ...prev, category: 'Required' }));
    }

    if (isNewItemDescriptionRequired()) {
      if (newItem.description) {
        setIsValidDescription(true);
      } else {
        setIsValidDescription(false);
        isValid = false;
        setErrorMessage(prev => ({ ...prev, description: 'Required' }));
      }
    } else {
      setIsValidDescription(true);
      setErrorMessage(prev => ({ ...prev, description: '' }));
    }

    if (newItem.quantity && newItem.quantity > 0) {
      setIsValidCount(true);
    } else {
      setIsValidCount(false);
      isValid = false;
      setErrorMessage(prev => (newItem.quantity < 0 ? { ...prev, quantity: "Can't be negative" } : { ...prev, quantity: 'Required' }));
    }

    if (isValid) {
      const updatedItems = [
        ...distItems,
        {
          product_id: newItem.product_id,
          id: newItem.id || nanoid(),
          category: newItem.category,
          description: newItem.description,
          quantity: newItem.quantity,
        },
      ];
      setDistItems(updatedItems);
      setNewItem({ category: '', description: '', quantity: '' });
      setIsAdding(false);
      setErrorMessage({ category: '', description: '', quantity: '' });
    }
  };

  const handleCancel = () => {
    setNewItem({ category: '', description: '', quantity: '' });
    setIsAdding(false);
    setIsValidItem(true);
    setIsValidDescription(true);
    setIsValidCount(true);
    setErrorMessage({ category: '', description: '', quantity: '' });
  };

  const handleNewItemChange = (field, value, optionId) => {
    const updatedItem = { ...newItem, [field]: value, id: optionId ?? newItem.id };
    setNewItem(updatedItem);
    if (field === 'category') {
      setIsValidItem(true);
      if (!isOtherCategory({ category: value })) {
        setIsValidDescription(true);
        setErrorMessage(prev => ({ ...prev, description: '' }));
      }
    }
    if (field === 'description') setIsValidDescription(true);
    if (field === 'quantity') setIsValidCount(true);
  };

  const handleItemChange = (id, value) => {
    const newItems = distItems.map(item => (item.id === id ? { ...item, category: value.label, product_id: value.value } : item));
    setDistItems(newItems);
    setIsValidItem(true);
    setErrorMessage(prev => ({ ...prev, category: '' }));
  };

  const handleDescriptionChange = (id, value) => {
    const newItems = distItems.map(item => (item.id === id ? { ...item, description: value } : item));
    setDistItems(newItems);
    setIsValidDescription(true);
    setErrorMessage(prev => ({ ...prev, description: '' }));
  };

  const handleCountChange = (id, value) => {
    const newItems = distItems.map(item => (item.id === id ? { ...item, quantity: value } : item));
    setDistItems(newItems);
    setIsValidCount(true);
    setErrorMessage(prev => ({ ...prev, quantity: '' }));
  };

  const getContainerClassName = () => {
    if (isEdit) return 'w-full';
    return isAdding ? 'w-full' : 'w-4/5';
  };

  const getOptions = (array) => array?.map(item => ({ label: item.name, value: item.id }));
  const fieldOptions = getOptions(options);

  return (
    <div className={`${headerBottomMargin || 'mb-5'}`}>
      <HeaderIconTooltip
        heading="Distributed Items"
        spanTextStart="Vamos Managers can configure your organization's list of item categories in the"
        linkUrl="/admin/items_for_distribution"
        linkText="Items for Distribution page."
        place="right"
        iconName="icon_circle"
        id="distItems"
        iconSize={iconSize}
        labelFontClassName={labelFontClassName}
        border={border}
        margins={margins}
        paddingBottom={paddingBottom}
      />
      <div className={getContainerClassName()}>
        <table className="w-full">
          {showHeader && (
          <thead key="table-header">
            <tr
              className="w-full text-md font-semibold tracking-wide h-13 text-left text-vamos-black bg-vamos-v-light-gray uppercase"
              key="table-header-row"
            >
              <th className="px-2 py-3 w-1/3">CATEGORY</th>
              <th className="px-2 py-3 w-2/5">DESCRIPTION</th>
              <th className="px-2 py-3 w-1/5">COUNT</th>
              {isEdit && <th className="px-2 py-3 whitespace-nowrap">ACTION</th>}
            </tr>
          </thead>
          )}
          <tbody key="table-body">
            {distItems.map((item) => (
              <tr
                className={`text-vamos-black h-13 even:bg-vamos-v-light-gray ${isEdit && 'hover:bg-vamos-green2 cursor-pointer'}`}
                key={item.id}
              >
                <td className="align-top px-2 py-3 w-1/3">
                  {isEdit ? (
                    <CustomSelect
                      value={item.category}
                      setValue={(value) => handleItemChange(item.id, value)}
                      options={fieldOptions}
                      emptyLabel="select category"
                      isRequired
                      errorMessage={item.category ? '' : errorMessage.category}
                      spaceY="space-y-0"
                    />
                  ) : (
                    <span>{item.category || 'N/A'}</span>
                  )}
                </td>
                <td className="align-top px-2 py-3 w-2/5">
                  {isEdit ? (
                    <TextInput
                      value={item.description}
                      setValue={(value) => handleDescriptionChange(item.id, value)}
                      placeholder="enter description"
                      isRequired={isOtherCategory(item)}
                      errorMessage={isOtherCategory(item) && !item.description ? errorMessage.description : ''}
                      errorMessageClass="absolute"
                      spaceY="space-y-0"
                    />
                  ) : (
                    <span>{item.description || 'N/A'}</span>
                  )}
                </td>
                <td className="align-top px-2 py-3 w-1/5">
                  {isEdit ? (
                    <Number
                      value={item.quantity}
                      setValue={(value) => handleCountChange(item.id, value)}
                      item={`quantity-${item.id}`}
                      placeholder="Quantity"
                      isRequired
                      min={0}
                      errorMessage={item.quantity ? '' : errorMessage.quantity}
                    />
                  ) : (
                    <span>{item.quantity || 'N/A'}</span>
                  )}
                </td>
                {isEdit && (
                <td className="align-top px-2 py-3 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="bg-transparent border-none p-0 cursor-pointer"
                  >
                    <Icon
                      name="delete"
                      className="text-vamos-black cursor-pointer"
                    />
                  </button>
                </td>
                )}
              </tr>
            ))}
            {isAdding && (
            <tr className="w-full text-vamos-black hover:bg-vamos-green2 cursor-pointer h-13 even:bg-vamos-v-light-gray">
              <td className="align-top px-2 py-3 w-1/3">
                <CustomSelect
                  value={newItem.category}
                  setValue={(value) => {
                    handleNewItemChange('category', value.label, value.value);
                    setErrorMessage(prev => ({ ...prev, category: '' }));
                  }}
                  options={fieldOptions}
                  emptyLabel="select category"
                  isRequired
                  isValid={isValidItem}
                  className="border-1"
                  borderColor="border-vamos-black"
                  errorMessage={newItem.category ? '' : errorMessage.category}
                  spaceY="space-y-0"
                />
              </td>
              <td className="align-top px-2 py-3 w-2/5">
                <TextInput
                  value={newItem.description}
                  setValue={(value) => {
                    handleNewItemChange('description', value);
                    setErrorMessage(prev => ({ ...prev, description: '' }));
                  }}
                  placeholder="enter description"
                  isRequired={isNewItemDescriptionRequired()}
                  isValid={isValidDescription}
                  className="border-1"
                  borderColor="border-vamos-black"
                  errorMessageClass="absolute"
                  errorMessage={isNewItemDescriptionRequired() ? errorMessage.description : ''}
                  spaceY="space-y-0"
                />
              </td>
              <td className="align-top px-2 py-3 w-1/5">
                <Number
                  value={newItem.quantity}
                  setValue={(value) => {
                    handleNewItemChange('quantity', value);
                    setErrorMessage(prev => ({ ...prev, quantity: '' }));
                  }}
                  item={`quantity-${newItem.id}`}
                  placeholder="Quantity"
                  isRequired
                  isValid={isValidCount}
                  className="border-1 w-full"
                  borderColor="border-vamos-black"
                  min={0}
                  errorMessage={errorMessage.quantity}
                />
              </td>
              <td className="align-top px-2 py-3 whitespace-nowrap flex flex-row">
                <button
                  type="button"
                  onClick={handleAdd}
                  className="bg-vamos-black text-white px-3 py-1 rounded mr-2 hover:bg-vamos-green1 h-10"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-vamos-white border border-vamos-black text-vamos-black px-3 py-1 rounded hover:bg-vamos-green3 h-10"
                >
                  Cancel
                </button>
              </td>
            </tr>
            )}
          </tbody>
        </table>
        {isEdit && !isAdding && (
        <button
          type="button"
          onClick={() => {
            setIsAdding(true);
            setShowHeader(true);
          }}
          className="w-full h-12 text-vamos-black bg-vamos-white px-2 py-1 rounded-md border border-vamos-black cursor-pointer mt-2"
        >
          + Item
        </button>
        )}
      </div>
    </div>
  );
};

DistributedItems.propTypes = {
  isEdit: PropTypes.bool.isRequired,
  options: PropTypes.array.isRequired,
  distItems: PropTypes.array,
  setDistItems: PropTypes.func,
  isShowHeader: PropTypes.bool,
  iconSize: PropTypes.string,
  labelFontClassName: PropTypes.string,
  border: PropTypes.string,
  headerBottomMargin: PropTypes.string,
  margins: PropTypes.string,
  paddingBottom: PropTypes.string,
};

export default DistributedItems;
